import * as React from "react";
import SaasApp from "./saasApp";
import "../../style/signup-region.module.css";
import "../../style/dashboard/common.module.css";
import { getAnalytics, getStripeRedirectURLs } from "../../utils";
import getSupportedRegions from "../../api/regions";
import { SaasAppListItem, SaasConnectionUrlDomain } from "./types";
import { getListSaasApps } from "../../api/saas/apps";
import Storage from "../../utils/storage";
import { clearDeploymentData } from "../../api/saas/deployment/db/delete";
import { PopupContentContext, PopupContentContextProps } from "../../context/PopupContentContext";
import saasAnalytics from "./saasAnalytics";
import sendInterestedFeatures from "../../api/website/features/interested/post";
import { CreateNewApp } from "./createApp";
import "../../style/dashboard-saas.module.css";
import { deleteSaasApp } from "../../api/saas/appInfo";
import { createMultiTenancyApp } from "../../api/saas/multi-tenancy/connection-uri-domain/post";
import { getMultiTenantApps } from "../../api/saas/multi-tenancy/connection-uri-domain/list";
import { convertSaasAppDataToNewFormat } from "./utils";
import { clearDbInfoForMultiTenantApp } from "../../api/saas/multi-tenancy/connection-uri-domain/appid/db/delete";
import { removeMultiTenancyApp } from "../../api/saas/multi-tenancy/connection-uri-domain/delete";
import { CustomEventNames } from "../../constants";
import CreateNewAppOrEnv from "./createNewAppOrEnv";
import DeleteAppOrEnv from "./createNewAppOrEnv/deleteAppOrEnv";
import { openPaymentModal } from "../../context/PaymentsModalContext";
import { PaymentsError } from "../../utils/customErrors";
import { PaymentModalBody } from "../../components/modals/payments-modals/PaymentsModal";
import EnablePaidFeaturesModal from "../../components/modals/paid-features-modal/EnablePaidFeaturesModal";
import SaasHeader from "../../components/Header";
import { Dropdown } from "../../components/dropdown";

type TabId = "SETUP" | "APP" | "NONE";

type DashboardSassExistState = {
    status: "EXIST";
    appDetails: SaasAppListItem;
    selectedTab: TabId;
    regions: string[];
};

type DashboardSaasStateWithRegions =
    | {
          status: "LOADING";
          regions?: string[];
          selectedTab: TabId;
      }
    | {
          status: "NO_APP_FOUND";
          regions: string[];
          selectedTab: TabId;
      };

type DashboardSaasPaymentRequiredState = {
    status: "PAYMENT_REQUIRED";
    selectedTab: TabId;
};

type DashboardSaasState = (
    | DashboardSaasStateWithRegions
    | DashboardSaasPaymentRequiredState
    | {
          status: "CREATING_APP";
          selectedTab: TabId;
      }
    | DashboardSassExistState
    | {
          status: "ERROR";
          selectedTab: TabId;
      }
    | {
          status: "RECREATING_APP";
          selectedTab: TabId;
      }) & {
    selectedRegion?: string;
    isDestroying?: boolean;
};

export const APP_ID_STORAGE_KEY = "user_selected_appid";
const CUD_STORAGE_KEY = "user_selected_cud";

export default class DashboardSaas extends React.Component<{}, DashboardSaasState> {
    headingRef: null;
    setHeadingRef: (element: any) => void;
    scrollPage: () => void;
    saasAppList: SaasAppListItem[];
    saasCUDList: SaasConnectionUrlDomain[];
    selectedCUD: SaasConnectionUrlDomain;

    constructor(props: {}) {
        super(props);
        this.state = {
            status: "LOADING",
            selectedTab: "NONE",
            isDestroying: false
        };

        this.headingRef = null;
        this.setHeadingRef = (element: any) => {
            this.headingRef = element;
            this.scrollPage();
        };

        this.scrollPage = () => {
            if (this.headingRef) {
                ((this.headingRef as unknown) as Element).scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        };
    }

    async componentDidMount() {
        try {
            await this.fetchRegions();
            await this.fetchAppDetails();
        } catch (e) {
            this.setState(() => ({
                status: "ERROR"
            }));
        }
    }

    onRegionSelected = async (region: string) => {
        try {
            getAnalytics().then((stAnalytics: any) =>
                stAnalytics.sendEvent(
                    "button_clouddetails_next",
                    {
                        region,
                        type: "button_click"
                    },
                    "v5"
                )
            );

            if (this.state.status !== "NO_APP_FOUND") {
                return;
            }
            this.setState({ status: "CREATING_APP" });
            const { cancelURL, successURL } = getStripeRedirectURLs();
            await createMultiTenancyApp({
                region: region,
                cancelURL,
                successURL
            });
            this.setState({ selectedRegion: region });
            const selectedAppId = Storage.getItem(APP_ID_STORAGE_KEY);
            this.fetchAppListAndSelectApp(
                () => ({ status: "ERROR", selectedTab: "NONE", selectedRegion: undefined }),
                selectedAppId
            );
        } catch (e) {
            this.setState({
                status: "ERROR"
            });
        }
    };

    resetEditState = (type: string) => {
        this.setState(prevState => {
            if (prevState.status !== "EXIST" || prevState.appDetails.isTemporarilyRemoved) {
                return {
                    ...prevState
                };
            }
            const { devDeployment, prodDeployment } = prevState.appDetails;
            if (type === "dev") {
                return {
                    ...prevState,
                    appDetails: {
                        ...prevState.appDetails,
                        devDeployment
                    }
                };
            } else {
                return {
                    ...prevState,
                    appDetails: {
                        ...prevState.appDetails,
                        prodDeployment
                    }
                };
            }
        });
    };

    updateAppDetails = (newAppDetails: any, type: string) => {
        if (this.state.status !== "EXIST") {
            return;
        }

        this.setState(prevState => {
            if (prevState.status !== "EXIST" || prevState.appDetails.isTemporarilyRemoved) {
                return prevState;
            }
            const { devDeployment, prodDeployment } = prevState.appDetails;
            if (type === "dev") {
                return {
                    ...prevState,
                    appDetails: {
                        ...prevState.appDetails,
                        devDeployment: {
                            ...devDeployment,
                            ...newAppDetails
                        }
                    }
                };
            } else {
                return {
                    ...prevState,
                    appDetails: {
                        ...prevState.appDetails,
                        prodDeployment: {
                            ...prodDeployment,
                            ...newAppDetails
                        }
                    }
                };
            }
        });
        document.dispatchEvent(new Event(CustomEventNames.dashboard.onAppDataFetched));
    };

    updatePricingDetails = (pricingInfo: any) => {
        this.setState(prevState => {
            if (prevState.status !== "EXIST" || prevState.appDetails.isTemporarilyRemoved) {
                return prevState;
            }

            const { prodDeployment } = prevState.appDetails;
            return {
                ...prevState,
                appDetails: {
                    ...prevState.appDetails,
                    prodDeployment: {
                        ...prodDeployment,
                        pricing: {
                            ...(prodDeployment !== undefined ? prodDeployment.pricing : undefined),
                            ...pricingInfo
                        }
                    }
                }
            };
        });
    };

    fetchAppDetails = async () => {
        const selectedAppId = Storage.getItem(APP_ID_STORAGE_KEY);
        await this.fetchAppListAndSelectApp(
            (prevState: DashboardSaasStateWithRegions) => ({
                status: "NO_APP_FOUND",
                selectedTab: "SETUP",
                regions: prevState.regions || []
            }),
            selectedAppId
        );
    };

    /**
     * Gets App list and loads the selected `appId` or the first item from the list into state
     * @param onEmptyCallback callback to be executed when the list return empty
     * @param appId id that will be selected after fetching the list
     */
    async fetchAppListAndSelectApp(
        onEmptyCallback: (prevState?: Readonly<DashboardSaasState>) => DashboardSaasState,
        appId?: string
    ) {
        try {
            const { cancelURL, successURL } = getStripeRedirectURLs();
            const response = await getListSaasApps({
                cancelURL,
                successURL
            });
            const multiTenantResponse = await getMultiTenantApps({ cancelURL, successURL });

            if (response.length === 0) {
                this.saasCUDList = multiTenantResponse;
                if (multiTenantResponse.length === 0) {
                    this.saasAppList = [];
                } else {
                    let connectionUriDomain: SaasConnectionUrlDomain | undefined;

                    let lastSelectedCUDId = Storage.getItem(CUD_STORAGE_KEY);

                    if (lastSelectedCUDId !== undefined) {
                        let _connectionUriDomain = multiTenantResponse.find(
                            (cud: SaasConnectionUrlDomain) => cud.deploymentId === lastSelectedCUDId
                        );

                        if (_connectionUriDomain !== undefined) {
                            connectionUriDomain = _connectionUriDomain;
                        }
                    }

                    if (connectionUriDomain === undefined) {
                        connectionUriDomain = multiTenantResponse[0];
                    }

                    this.selectedCUD = connectionUriDomain;

                    if (connectionUriDomain.isTemporarilyRemoved === true) {
                        this.saasAppList = [convertSaasAppDataToNewFormat(connectionUriDomain)];
                    } else {
                        let finalSaasAppList: SaasAppListItem[] = [];

                        for (let i = 0; i < connectionUriDomain.apps.length; i++) {
                            const app = connectionUriDomain.apps[i];
                            finalSaasAppList = [
                                ...finalSaasAppList,
                                convertSaasAppDataToNewFormat(connectionUriDomain, app)
                            ];
                        }

                        this.saasAppList = finalSaasAppList;
                    }
                }
            } else {
                this.saasCUDList = [];

                if (multiTenantResponse.length > 0) {
                    for (let i = 0; i < response.length; i++) {
                        response[i].disableEditing = true;
                    }
                }

                this.saasAppList = response;
            }

            this.selectApp(onEmptyCallback, appId);
        } catch (error) {
            if (PaymentsError.isThisError(error)) {
                openPaymentModal(
                    {
                        action_type: "list-apps",
                        redirect_uri: error.redirectionURL,
                        data: null,
                        timestamp: Date.now()
                    },
                    true
                );
                this.setState({
                    ...this.state,
                    status: "PAYMENT_REQUIRED"
                });
            }
        }
    }

    fetchRegions = async () => {
        try {
            const response = await getSupportedRegions();
            if (response.regions.length > 0) {
                this.setState(prevState => {
                    return {
                        ...prevState,
                        regions: response.regions
                    };
                });
            }
            if (response.regions.length === 0) {
                this.setState({ status: "ERROR" });
            }
        } catch (error) {
            this.setState({ status: "ERROR" });
        }
    };

    onPageErrorRetryClicked = () => {
        window.location.reload();
    };

    private selectApp(
        onEmptyCallback: (prevState?: Readonly<DashboardSaasState>) => DashboardSaasState,
        appId?: string
    ) {
        this.setState(prevState => {
            const selectedAppFromAppId =
                appId !== undefined
                    ? this.saasAppList.find(app => appId === app.appId || appId === app.appName)
                    : undefined;
            if (selectedAppFromAppId == undefined && this.saasAppList.length > 0) {
                // only remove the APP_ID_STORAGE_KEY if the saasAppList is finish loading the API(not empty)
                Storage.removeItem(APP_ID_STORAGE_KEY);
            }
            let selectedApp: SaasAppListItem | undefined = undefined;
            if (selectedAppFromAppId !== undefined) {
                selectedApp = selectedAppFromAppId;
            } else {
                selectedApp = this.saasAppList[0];
            }

            // selectedApp.isTemporarilyRemoved && selectedApp.isRecreating implies that the app was paused but the
            // user used the recreate button. We set to creating state and refetch app data to check when it is up
            if (selectedApp !== undefined && selectedApp.isTemporarilyRemoved && selectedApp.isRecreating) {
                // We only poll for app data regularly if it already existed, this makes sure that we check for the app
                // being recreated succesfully so that the user is not stuck in a loading state forever
                setTimeout(() => {
                    this.fetchAppDetails();
                }, 5000);

                return {
                    status: "RECREATING_APP",
                    selectedTab: "NONE",
                    appDetails: undefined
                };
            }

            if (selectedApp !== undefined) {
                Storage.setItem(APP_ID_STORAGE_KEY, appId || selectedApp.appId);
                return {
                    status: "EXIST",
                    selectedTab: "APP",
                    appDetails: {
                        ...selectedApp,
                        devDeployment: selectedApp.isTemporarilyRemoved ? undefined : selectedApp.devDeployment,
                        prodDeployment: selectedApp.isTemporarilyRemoved ? undefined : selectedApp.prodDeployment
                    }
                };
            } else {
                return onEmptyCallback(prevState) as DashboardSaasState;
            }
        });
    }

    selectAppAndReload(appId: string) {
        Storage.setItem(APP_ID_STORAGE_KEY, appId);
        location.reload();
    }

    selectCUDAndReload(cudId: string) {
        Storage.setItem(CUD_STORAGE_KEY, cudId);
        location.reload();
    }

    clearDataForApp = async (deploymentId: string): Promise<any | undefined> => {
        if (this.state.status !== "EXIST") {
            return;
        }

        if (this.state.appDetails.isTemporarilyRemoved === true) {
            return;
        }

        if (this.state.appDetails.usesMultiTenancy !== true) {
            return await clearDeploymentData(this.state.appDetails.appId, deploymentId);
        } else {
            return await clearDbInfoForMultiTenantApp({
                appId: deploymentId,
                deploymentId: this.state.appDetails.appId
            });
        }
    };

    clearDataInDeployment = async (deploymentId: string) => {
        if (this.state.status === "EXIST") {
            const { showToast } = this.context as PopupContentContextProps;

            if (this.state.appDetails.disableEditing === true) {
                showToast({
                    status: "error",
                    icon: `static/assets/dashboard/${"alert"}.png`,
                    children: <>Editing is disabled for this app because it is undergoing maintenance.</>
                });
                return;
            }

            const succeed = (await this.clearDataForApp(deploymentId)) !== undefined;

            showToast({
                status: "error",
                icon: `static/assets/dashboard/${succeed ? "trash-red" : "alert"}.png`,
                children: succeed ? <>Data deleted successfully</> : <>Failed to delete data</>
            });
        }
    };

    dismissFeatureBanner(interestedFeatures: string[]) {
        const isInterested = interestedFeatures.length > 0;
        saasAnalytics.clickFeatureBannerCta(isInterested ? "I'm Interested in Buying" : "No, thanks");
        if (isInterested) {
            sendInterestedFeatures(interestedFeatures);
        }
    }

    getCreateApplicationStep() {
        if (this.state.status === "NO_APP_FOUND") {
            return (
                <>
                    <CreateNewApp regions={this.state.regions} onSubmit={this.onRegionSelected}>
                        {this.renderTabsHeader()}
                    </CreateNewApp>
                </>
            );
        }

        if (this.state.status === "EXIST") {
            let deploymentId: string | undefined;
            let apiKey: string | undefined;
            let usesMultiTenancy: boolean | undefined;
            let connectionHost: string | undefined;

            for (let i = 0; i < this.saasAppList.length; i++) {
                const currentApp = this.saasAppList[i];
                if (
                    currentApp.isTemporarilyRemoved !== true &&
                    currentApp.appName === "public" &&
                    currentApp.usesMultiTenancy &&
                    currentApp.devDeployment !== undefined
                ) {
                    deploymentId = currentApp.appId;
                    apiKey = currentApp.devDeployment.connectionInfo!.apiKeys[0];
                    connectionHost = currentApp.devDeployment.connectionInfo!.host;
                    usesMultiTenancy = true;
                }
            }
            return (
                <>
                    {this.saasAppList.length > 1 && (
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "32px" }}>
                            <span
                                style={{
                                    fontWeight: 500,
                                    marginRight: 8
                                }}
                            >
                                App ID:
                            </span>
                            <Dropdown<string>
                                defaultText="App ID:"
                                selectedValue={
                                    this.state.appDetails.usesMultiTenancy === true
                                        ? this.state.appDetails.appName
                                        : this.state.appDetails.appId
                                }
                                items={this.saasAppList.map(app => {
                                    if (app.usesMultiTenancy === true) {
                                        return { value: app.appName, text: app.appName };
                                    } else {
                                        return { value: app.appId, text: app.appName };
                                    }
                                })}
                                onChange={appId => {
                                    this.selectAppAndReload(appId);
                                }}
                            />
                        </div>
                    )}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 32,
                            justifyContent: "space-between"
                        }}
                    >
                        <EnablePaidFeaturesModal
                            appsList={this.saasAppList.map(app => {
                                if (app.isTemporarilyRemoved === false) {
                                    return {
                                        appName: app.appName,
                                        devDeployment: app.devDeployment,
                                        prodDeployment: app.prodDeployment
                                    };
                                } else {
                                    throw new Error("This case should never happen.");
                                }
                            })}
                            appId={this.state.appDetails.appName}
                            deploymentId={deploymentId}
                            isNewArchitecture={usesMultiTenancy === true}
                        />
                        {deploymentId && apiKey && connectionHost ? (
                            <div style={{ display: "flex", gap: "16px" }}>
                                <CreateNewAppOrEnv
                                    deploymentId={deploymentId}
                                    apiKey={apiKey}
                                    connectionHost={connectionHost}
                                />
                                <DeleteAppOrEnv appName={this.state.appDetails.appName} deploymentId={deploymentId} />
                            </div>
                        ) : null}
                    </div>
                    {this.state.selectedTab === "SETUP" && (
                        <CreateNewApp
                            regions={this.state.regions}
                            showSubmit={false}
                            onSubmit={this.onRegionSelected}
                            shouldAddShadow
                        >
                            {this.renderTabsHeader()}
                        </CreateNewApp>
                    )}

                    {this.state.selectedTab === "APP" && (
                        <SaasApp
                            appDetails={this.state.appDetails}
                            appList={this.saasAppList}
                            refetchAppDetails={this.fetchAppDetails}
                            updatePricingDetails={this.updatePricingDetails}
                            updateAppDetails={this.updateAppDetails}
                            resetEditState={this.resetEditState}
                            selectApp={this.selectAppAndReload}
                            clearDeploymentData={this.clearDataInDeployment}
                            dismissFeatureBanner={this.dismissFeatureBanner}
                        >
                            {this.renderTabsHeader()}
                        </SaasApp>
                    )}
                </>
            );
        }
    }

    renderTabsHeader() {
        return (
            <>
                {this.state.selectedTab !== "NONE" && (
                    <div className="tabs-header">
                        {this.state.status === "EXIST" && this.renderTabHeaderItem("Core configuration Details", "APP")}
                    </div>
                )}
            </>
        );
    }

    renderContent() {
        const { status } = this.state;
        if (status === "LOADING") {
            return (
                <>
                    <div
                        style={{
                            width: "100%",
                            minHeight: "30vh",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <div className="st-spinner" />
                    </div>
                </>
            );
        }

        if (status === "ERROR") {
            return (
                <>
                    <div id="dashboard-error-root">
                        <div id="dashboard-error-text">Something went wrong</div>

                        <div onClick={this.onPageErrorRetryClicked} id="dashboard-error-button">
                            Try again
                        </div>
                    </div>
                </>
            );
        }

        if (status === "CREATING_APP") {
            return (
                <>
                    <div ref={this.setHeadingRef} className="saas-dashboard-animation">
                        {"Creating an instance. It might take 15-20 seconds. Please wait "}
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                    </div>
                </>
            );
        }

        if (status === "PAYMENT_REQUIRED") {
            return (
                <div className="payment-wall-container">
                    <PaymentModalBody />
                </div>
            );
        }

        if (status === "RECREATING_APP") {
            return (
                <>
                    <div ref={this.setHeadingRef} className="saas-dashboard-animation">
                        {"Creating an instance. It might take up to a few minutes. Please wait "}
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                    </div>
                </>
            );
        }

        return <>{this.getCreateApplicationStep()}</>;
    }

    renderTabHeaderItem = (text: string, tabId: TabId) => {
        const isSelected = this.state.selectedTab === tabId;
        return (
            <div
                className={`tab-header-item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                    if (isSelected) {
                        return;
                    }

                    this.setState({ selectedTab: tabId });
                }}
            >
                {text}

                {isSelected && <div className="selected-highlight" />}
            </div>
        );
    };

    handleSaasAppDelete = async () => {
        if (this.state.status !== "EXIST") {
            return;
        }

        if (this.state.isDestroying === true) {
            return;
        }

        // if (this.state.appDetails.isTemporarilyRemoved !== false) {
        //     return;
        // }

        this.setState(oldState => {
            return {
                ...oldState,
                isDestroying: true
            };
        });

        // This is used tp pause pinging for app info while the app is being deleted
        Storage.setItem("saas-app-delete-action", "true");
        if (this.state.appDetails.usesMultiTenancy !== true) {
            await deleteSaasApp(this.state.appDetails.appId);
        } else {
            await removeMultiTenancyApp({
                deploymentId: this.state.appDetails.appId
            });
        }

        Storage.removeItem("saas-app-delete-action");

        window.location.reload();
    };

    render() {
        const shouldTabContainerHaveShadow = this.state.status !== "EXIST";

        let tabsContainerClasses = ["tabs-container", "with-margin"];

        if (shouldTabContainerHaveShadow) {
            tabsContainerClasses.push("with-shadow");
        }

        if (this.state.status !== "EXIST" && this.state.status !== "NO_APP_FOUND") {
            tabsContainerClasses.push("with-background with-padding");
        }

        const regionFromState = this.state.selectedRegion;
        let regionFromCurrentApp: string | undefined = undefined;

        if (this.state.status === "EXIST" && this.state.appDetails.isTemporarilyRemoved !== true) {
            regionFromCurrentApp = this.state.appDetails.region;
        }

        const regionToRender = regionFromState !== undefined ? regionFromState : regionFromCurrentApp;

        return (
            <div className="saas-container">
                <div>
                    <div className="centered-box">
                        <div className="announcement-container">
                            <span className="announcement-blue-pill centered">INFO</span>
                            <div className="announcement-content">
                                <b>SuperTokens core</b> is an HTTP service responsible for handling essential authentication
                                tasks such as user sign-up, sign-in, and session management. It interacts with the
                                backend SDK to perform these various operations.
                            </div>
                        </div>
                        {this.state.status === "EXIST" &&
                            (window.location.hostname === "localhost" ||
                                window.location.hostname === "test.supertokens.com") && (
                                <button
                                    className="delete-app-button"
                                    onClick={this.handleSaasAppDelete}
                                    disabled={this.state.isDestroying === true}
                                >
                                    {this.state.isDestroying === true ? "Deleting..." : "Delete app"}
                                </button>
                            )}
                        {this.state.status === "EXIST" && this.saasCUDList.length > 1 && (
                            <Dropdown<string>
                                defaultText="App ID:"
                                selectedValue={this.selectedCUD.deploymentId}
                                items={this.saasCUDList.map(({ deploymentId, deploymentName }) => ({
                                    value: deploymentId,
                                    text: deploymentName
                                }))}
                                onChange={appId => {
                                    this.selectAppAndReload(appId);
                                }}
                            />
                        )}
                        <SaasHeader title="SuperTokens Managed Service" region={regionToRender} />
                    </div>
                    {this.state.status === "NO_APP_FOUND" ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: 32,
                                justifyContent: "space-between",
                                width: "60vw",
                                maxWidth: "1000px",
                                minWidth: "50rem"
                            }}
                        >
                            <EnablePaidFeaturesModal
                                appsList={[]}
                                appId={undefined}
                                deploymentId={this.selectedCUD?.deploymentId}
                                isNewArchitecture={true}
                            />
                        </div>
                    ) : null}
                    <div className={`${tabsContainerClasses.join(" ")}`}>{this.renderContent()}</div>
                </div>
            </div>
        );
    }
}

DashboardSaas.contextType = PopupContentContext;
